import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, makeStyles } from '@material-ui/core';
import type { Theme } from '@material-ui/core';

import { useCurrentUser } from '@klp/auth';
import { Conditional } from '@klp/conditional';
import { Block, Heading } from '@klp/design';
import { DateTime } from '@klp/design/DateTime';
import { Loader } from '@klp/design/Loader';
import type { ITableConsumerState } from '@klp/design/Table';
import { Table, TableColumnDef } from '@klp/design/Table';
import { Main } from '@klp/layout';
import { Search } from '@klp/search';

import type { User } from '../classes/User';
import {
    AddCustomer,
    RemoveCustomer,
    SendWelcomeMessage,
    ToggleAdmin,
    ToggleSuperUser,
} from '../components';
import {
    UserSearchAccess,
    UserEditAccess,
    UserToggleAdminAccess,
    UserToggleSuperUserAccess,
} from '../config';
import { useSearchUsersQuery } from '../rtkApi';

const useStyles = makeStyles((theme: Theme) => ({
    searchResult: {
        '&:hover': {
            backgroundColor: theme.palette.primary.veryLight,
        },
    },
    alignCenter: {
        textAlign: 'center' as const,
    },
}));

export const SearchUsers: React.FC = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [searchQuery, setSearchQuery] = useState('');
    const { data: searchResult = [], isLoading: searchLoading } =
        useSearchUsersQuery(searchQuery, { skip: !searchQuery });
    const { currentUser: user } = useCurrentUser();

    const handleSearch = (search: string) => {
        setSearchQuery(search);
    };

    const updateSearch = () => {
        // Trigger refetch by updating the query
        setSearchQuery((prev) => prev);
    };

    useEffect(() => {
        return () => {
            setSearchQuery('');
        };
    }, []);

    const renderWelcomeMessageSent = (value: Date) => (
        <Conditional
            condition={value !== undefined}
            errorComponent={<>{t('userSearch.invitationNotSent')}</>}
        >
            <DateTime date={value} format={DateTime.format.DateTime} />
        </Conditional>
    );

    const renderSendNewWelcomeMessage = (value: string) => (
        <SendWelcomeMessage email={value} onSuccess={updateSearch} />
    );

    const renderIsAdminUser = <DataType, FilterType>(
        value: boolean,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<User, unknown>,
        row: User,
    ) => (
        <Block justifyContent="center">
            <ToggleAdmin
                email={row.email}
                isAdmin={value}
                onSuccess={updateSearch}
            />
        </Block>
    );

    const renderIsSuperUser = <DataType, FilterType>(
        value: boolean,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<User, unknown>,
        row: User,
    ) => (
        <Block className={classes.alignCenter} justifyContent="center">
            <ToggleSuperUser
                email={row.email}
                isSuperUser={value}
                onSuccess={updateSearch}
            />
        </Block>
    );

    const renderUserCustomer = (customerNumber: string, email: string) => {
        return (
            <Fragment key={customerNumber}>
                <RemoveCustomer
                    disabled={
                        !user?.customers.includes(customerNumber) ||
                        user.email === email
                    }
                    customerNumber={customerNumber}
                    email={email}
                    onSuccess={updateSearch}
                />
            </Fragment>
        );
    };

    const renderUserCustomers = <DataType, FilterType>(
        value: Array<string>,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<User, unknown>,
        row: User,
    ) => {
        return (
            <Block justifyContent="center">
                {value.map((customerNumber) =>
                    renderUserCustomer(customerNumber, row.email),
                )}
                <AddCustomer
                    selectedUser={row}
                    availableCustomers={user?.customers || []}
                    onSuccess={updateSearch}
                />
            </Block>
        );
    };

    const getColumns = (
        hasKundeportalInteresse = true,
    ): Array<TableColumnDef<User, unknown>> => {
        const columns = [
            new TableColumnDef<User, unknown>({
                key: 'firstName',
                translation: 'userSearch.userFirstName',
                align: 'left',
            }),
            new TableColumnDef<User, unknown>({
                key: 'lastName',
                translation: 'userSearch.userLastName',
                align: 'left',
            }),
            new TableColumnDef<User, unknown>({
                key: 'email',
                translation: 'userSearch.userEmail',
                align: 'left',
            }),
        ];

        if (hasKundeportalInteresse) {
            columns.push(
                new TableColumnDef<User, unknown>({
                    key: 'welcomeMessageSent',
                    translation: 'userSearch.welcomeMessageSent',
                    render: renderWelcomeMessageSent,
                }),
            );
        }

        if (hasKundeportalInteresse && UserEditAccess(user)) {
            columns.push(
                new TableColumnDef<User, unknown>({
                    key: 'email',
                    translation: 'userSearch.sendNewWelcomeMessage',
                    render: renderSendNewWelcomeMessage,
                }),
            );
        }

        if (UserToggleAdminAccess(user)) {
            columns.push(
                new TableColumnDef<User, unknown>({
                    key: 'isAdmin',
                    translation: 'userSearch.userIsAdmin',
                    render: renderIsAdminUser,
                }),
            );
        }

        if (UserToggleSuperUserAccess(user)) {
            columns.push(
                new TableColumnDef<User, unknown>({
                    key: 'isSuperUser',
                    translation: 'userSearch.userIsSuperUser',
                    render: renderIsSuperUser,
                }),
            );
        }

        if (
            hasKundeportalInteresse &&
            user?.customers &&
            user.customers.length > 1
        ) {
            columns.push(
                new TableColumnDef<User, unknown>({
                    key: 'customers',
                    translation: 'userSearch.userCustomers',
                    render: renderUserCustomers,
                }),
            );
        }

        return columns;
    };

    return (
        <Main>
            <Conditional
                condition={UserSearchAccess(user)}
                /* eslint-disable-next-line react/jsx-no-useless-fragment */
                errorComponent={<Fragment />}
            >
                <Heading
                    klpHeadingLevel="THREE"
                    translation="userSearch.searchTitle"
                    marginBottom="compact"
                />
                <Grid container={true}>
                    <Grid item={true} xs={12} sm={6}>
                        <Search
                            id="UserSearch"
                            searchHandler={handleSearch}
                            title="userSearch.searchTitle"
                            placeholder="userSearch.searchPlaceholder"
                        />
                    </Grid>
                </Grid>

                {searchQuery && (
                    <Loader isLoading={searchLoading}>
                        <Block marginVertical="fat">
                            <Heading
                                klpHeadingLevel="THREE"
                                translation="userSearch.activeUsers"
                            />
                            <Table<User, unknown>
                                id="SearchUsers"
                                emptyTableMessage="clientSelect.notFound"
                                initialRowsPerPage={10}
                                data={searchResult.filter(
                                    (u) => u.hasKundeportalInteresse,
                                )}
                                columns={getColumns()}
                                highlightPhrase={searchQuery}
                            />
                        </Block>
                        <Block marginVertical="fat">
                            <Heading
                                klpHeadingLevel="THREE"
                                translation="userSearch.inActiveUsers"
                            />
                            <Table<User, unknown>
                                id="SearchUsers"
                                emptyTableMessage="clientSelect.notFound"
                                initialRowsPerPage={10}
                                data={searchResult.filter(
                                    (u) => !u.hasKundeportalInteresse,
                                )}
                                columns={getColumns(false)}
                                highlightPhrase={searchQuery}
                            />
                        </Block>
                    </Loader>
                )}
            </Conditional>
        </Main>
    );
};
